import {
  useFormFieldGroup,
  useSelectField,
  useField,
  useFormSection,
  useTextField
} from "@/components/FormBuilder/Helpers";
import {
  yearOptions,
  yearBuiltOptions,
  liabilityLimit,
  medLiability,
  occupancyOptions,
  limitDed1OptionsCompany,
  limitDed1OptionsCompanyGreaterThan4,
  yesNoOptions,
  waterLimitOptions,
  limitedSlabFoundationOptions,
  billingPlanOptions
} from "@/helpers/selectOptions";
import sharedUnderwritingQuestions from "@/forms/shared/quote.shared_underwriter_questions";

import { ICompany } from "../../store/modules/companies/types";
import {
  companyNumber28Or30,
  hasSeacoastSeaTerrData,
  hasColonialStateWide,
  mCompanyBetween1And3,
  onlySeacoastSelected
} from "../utils";
import { underWriterApprovalSchema } from "./quote.dwelling_fire";
import {
  hasColonialSeacoast,
  hasCompanyNumber31,
  hasInsurorsIndemnity,
  isPoolInApprovedFence,
  occupancyValidator
} from "../utils/quotes";
import { isValidEffectiveDate } from "@/helpers/validateEffectiveDate";
import { IUserModel } from "@/types";
import { ICompanyTerritories } from "@/store/modules/gbshelp/types";
export const paymentPlanOptions = [
  { label: "Bill Insured", value: "1" },
  { label: "Bill Mortgagee(in full only)", value: "2" }
];

export const numberOfNonWeatherClaimsOptions = [0, 1, 2, 3].map(
  (op: number) => ({
    value: op,
    label: op.toString()
  })
);

export function getHomeOwnerLimitedDedOptions(companies: ICompany[]) {
  let homeOwnerLimitDed1Options = limitDed1OptionsCompany;
  // iterate through the companies and check if mCompany is greater than 4
  const mCompanyGreaterThan4 =
    companies &&
    companies.length > 0 &&
    companies.filter(company => company.mCompany >= 4).length > 0
      ? true
      : false;
  if (mCompanyGreaterThan4) {
    // check if homeOwnerLimitDed1OptionsCompanyGreaterThan4 does not exist in homeOwnerLimitDed1Options
    if (
      limitDed1OptionsCompanyGreaterThan4.every(
        option =>
          !homeOwnerLimitDed1Options.some(
            (homeOwnerLimitDed1Option: any) =>
              homeOwnerLimitDed1Option.value === option.value
          )
      )
    ) {
      homeOwnerLimitDed1Options = [
        ...limitDed1OptionsCompanyGreaterThan4,
        ...homeOwnerLimitDed1Options
      ];
    }
  }

  return homeOwnerLimitDed1Options;
}

function checkForEndorsements(endorsements: any[]) {
  if (endorsements) {
    const endorsementsArray = endorsements.map(endorsement => {
      return endorsement.formCode;
    });
    if (
      endorsementsArray.includes("470C") &&
      endorsementsArray.includes("849")
    ) {
      return true;
    }
  }
  return false;
}

export interface ICompaniesData {
  companyNumbers: any[];
  billingValue: string;
  user: IUserModel;
  createdOn: Date;
  policyType?: string;
  endorsements?: any[];
  companyTerritories?: ICompanyTerritories[];
  companies?: ICompany[];
}

export const roofMaterialOptions = function(data: ICompaniesData) {
  const { companies, companyTerritories, companyNumbers } = data;
  let roofMaterials = [
    { label: "Composition", value: "6" },
    { label: "Tile", value: "7" },
    { label: "Metal", value: "8" },
    { label: "All Others", value: "9" }
  ];

  if (
    companyNumbers.includes(32) &&
    companyTerritories &&
    hasSeacoastSeaTerrData(companyTerritories)
  ) {
    return roofMaterials;
  } else if (
    (mCompanyBetween1And3(companies!) ||
      companyNumber28Or30(companyNumbers) ||
      hasColonialStateWide(companyNumbers)) &&
    !hasColonialSeacoast(companyNumbers)
  ) {
    roofMaterials.splice(1, 1); //Idk why a comment wasn't added. this removes title with value 7. current options for roofMaterialOptions { label: "Composition", value: "6" }, { label: "Tile", value: "7" }, { label: "Metal", value: "8" }, { label: "All Others", value: "9" }
    roofMaterials.pop(); //this removes "all others" options
  } else if (
    companyTerritories &&
    onlySeacoastSelected(companyNumbers) &&
    !hasSeacoastSeaTerrData(companyTerritories)
  ) {
    roofMaterials.splice(1, 3); //this removes the next 3 elements starting from title since title index is 1, leaving Composition
  }
  if (hasColonialStateWide(companyNumbers)) {
    roofMaterials = roofMaterials.filter(option => {
      return option.label.toLowerCase() !== "metal";
    });
  }
  if (companyNumbers.length === 1 && hasCompanyNumber31(companyNumbers)) {
    roofMaterials = roofMaterials.filter(option => {
      return option.label.toLowerCase() !== "tile";
    });
  }

  return roofMaterials;
};

const namedStormDeductibleOptions = [
  { label: "2 %", value: "2.0000" },
  { label: "2.5 %", value: "2.5000" },
  { label: "3 %", value: "3.0000" },
  { label: "4 %", value: "4.0000" },
  { label: "5 %", value: "5.0000" }
];

export default function(data: ICompaniesData) {
  const {
    companies,
    companyNumbers,
    user,
    policyType,
    endorsements,
    createdOn,
    companyTerritories
  } = data;

  const homeOwnerLimitDedOptions = getHomeOwnerLimitedDedOptions(companies!);

  const baseStructureTypeOptions = [
    { label: "Brick", value: "M" },
    { label: "Brick Veneer", value: "B" },
    { label: "Stucco", value: "A" },
    { label: "Frame", value: "F" }
  ];

  const billingOptions = billingPlanOptions;
  const structureTypeOptions = baseStructureTypeOptions;
  if (companyNumbers?.includes(99)) {
    structureTypeOptions.push({ label: "Asbestos Siding", value: "S" });
  }

  if (onlySeacoastSelected(companyNumbers)) {
    billingOptions.pop();
  }

  const isSeacoast =
    companyTerritories && hasSeacoastSeaTerrData(companyTerritories);

  const homeOwnerForm = [
    ...useFormFieldGroup(
      [
        useField({
          key: "effectiveDate",
          label: "Effective Date",
          required: true,
          type: "date",
          pickerOptions: {
            disabledDate: (fieldValue: Date) =>
              !isValidEffectiveDate(fieldValue, createdOn, user)
          }
        }),
        useSelectField({
          key: "paymentPlan",
          label: "Payment Plan",
          options: paymentPlanOptions,
          required: true
        }),
        useSelectField({
          key: "billing",
          label: "Billing Plan",
          options: billingOptions,
          required: true,
          conditions: {
            and: [
              {
                field: "paymentPlan",
                value: "1",
                operator: "equals"
              }
            ]
          }
        })
      ],
      { layout: "3-col" }
    ),
    ...useFormSection(
      [
        ...useFormSection(
          [
            ...useFormFieldGroup(
              [
                useSelectField({
                  key: "useAge",
                  label: "Property Usage",
                  options: [
                    { label: "Primary", value: "0" },
                    { label: "Secondary", value: "1" },
                    { label: "Seasonal", value: "2" }
                  ],
                  required: true
                }),
                useSelectField({
                  key: "dwellType",
                  label: "Dwelling Type",
                  options: [
                    { label: "Dwelling", value: "0" },
                    { label: "Townhouse", value: "1" },
                    { label: "Duplex", value: "2" }
                  ],
                  required: true
                }),
                useSelectField({
                  key: "structureType",
                  label: "Building Structure Type",
                  options: structureTypeOptions,
                  required: true
                })
              ],
              { layout: "3-col" }
            ),
            ...useFormFieldGroup(
              [
                useSelectField({
                  key: "occupancy",
                  label: "Occupancy",
                  options: occupancyOptions,
                  required: hasInsurorsIndemnity(companyNumbers),
                  validations: [
                    {
                      method: "custom",
                      error: "Risk is prohibited",
                      customValidator: occupancyValidator
                    }
                  ]
                })
              ],
              { layout: "3-col" }
            )
          ],
          {
            title: "Property Usage & Construction"
          }
        ),
        ...useFormSection(
          [
            ...useFormFieldGroup(
              [
                useSelectField({
                  key: "limitDed1",
                  label: "Wind/Hail",
                  options: homeOwnerLimitDedOptions.filter(
                    option => !["1.0000", "1.5000"].includes(option.value)
                  ),
                  required: true
                }),
                useSelectField({
                  key: "limitDed2",
                  label: "All Other",
                  options: homeOwnerLimitDedOptions,
                  required: true
                }),
                useSelectField({
                  key: "limitDed31",
                  label: "Named Storm Ded",
                  options: namedStormDeductibleOptions,
                  required: true,
                  information: {
                    title: "Reminder",
                    body: `Named Windstorm Deductible must be equal to or higher than the D1 Wind/Hail deductible`
                  },
                  conditions: {
                    or: [
                      {
                        field: "companyNumbers",
                        value: 31,
                        operator: "includes"
                      }
                    ]
                  }
                }),
                useSelectField({
                  key: "limitLiab",
                  label: "Liability Limit",
                  options: liabilityLimit,
                  required: true
                }),
                useSelectField({
                  key: "limitMed",
                  label: "Med Limit",
                  options: medLiability,
                  required: true
                })
              ],
              { layout: "4-col" }
            ),
            ...useFormFieldGroup(
              [
                useSelectField({
                  key: "roofMaterial",
                  label: "Roof Material",
                  options: roofMaterialOptions(data),
                  required: true,
                  information: {
                    title: "Reminder",
                    body: `As a reminder Insurors requires 100% composition shingle <br>
                      over all living areas`
                  },
                  conditions: {
                    or: [
                      {
                        field: "shouldAddRoofMaterial",
                        value: true,
                        operator: "equals"
                      }
                    ]
                  }
                })
              ],
              { layout: "4-col" }
            )
          ],
          {
            title: "Limits and Deductible Modifiers",
            description:
              "* Ded 1 Wind/Hurr/Hail * Ded 2 All Other Perils – “Not all Deductible limits apply to all companies.”"
          }
        ),
        ...useFormSection(
          [
            ...useFormFieldGroup(
              [
                useSelectField({
                  key: "yearBuild",
                  label: "Year Built",
                  options: yearBuiltOptions,
                  required: true,
                  filterable: true,
                  allowCreate: false,
                  allowFirstOption: true,
                  conditions: {
                    and: [
                      {
                        operator: "hasValue",
                        field: "companyNumbers",
                        value: true
                      }
                    ]
                  },
                  validations: [
                    {
                      method: "custom",
                      customValidator: (value: any) => {
                        if (!value) return true;
                        return !isNaN(value) && `${value}`.length === 4;
                      },
                      error: "Must be a year!"
                    }
                  ]
                }),
                useSelectField({
                  key: "yearOfRoof",
                  label: "Roof Year",
                  options: yearOptions,
                  required: true,
                  filterable: true,
                  allowFirstOption: true,
                  conditions: {
                    and: [
                      {
                        operator: "hasValue",
                        field: "companyNumbers",
                        value: true
                      }
                    ]
                  }
                }),
                useTextField({
                  label: "Square Feet",
                  key: "squareFeet",
                  required: true,
                  validations: [
                    {
                      method: "integer",
                      error: "Square feet must be numeric only"
                    }
                  ]
                })
              ],
              { layout: "3-col" }
            ),
            useField({
              key: "amountDWPCompanyOverrides",
              label: "Adjustments to building amount per company and form type",
              required: false,
              type: "company-overrides"
            }),
            ...underWriterApprovalSchema,

            useField(
              {
                type: "radio_button_group",
                preamble: "Number of Non-Weather or Non-Appliance Claims",
                key: "numberOfNonWeatherClaims",
                options: [0, 1, 2, 3].map((op: number) => ({
                  value: op,
                  label: op.toString()
                })),
                required: true
              },
              ["mb-8"]
            ),
            useField(
              {
                type: "radio_button_group",
                preamble:
                  "Are there any dogs from the restricted list or exotic pets on the premises?",
                key: "dogsOrExoticBreeds",
                options: yesNoOptions,
                required: true,
                conditions: {
                  or: [
                    {
                      field: "companyNumbers",
                      value: 20,
                      operator: "includes"
                    },
                    {
                      field: "companyNumbers",
                      value: 32,
                      operator: "includes"
                    }
                  ],
                  and: [
                    {
                      field: "terrSeacoast",
                      value: true,
                      operator: "equals"
                    }
                  ]
                }
              },
              ["mb-8"]
            ),
            useField(
              {
                type: "radio_button_group",
                preamble: "Dead Bolts?",
                key: "deadBolts",
                options: yesNoOptions,
                required: true,
                conditions: {
                  and: [
                    {
                      field: "hasSeacoastAndNotSeacoastSeaTerritory",
                      value: true,
                      operator: "equals"
                    }
                  ]
                }
              },
              ["mb-8"]
            ),
            useField(
              {
                type: "radio_button_group",
                preamble: "Fire Extinguishers?",
                key: "fireExtinguisherOnPremises",
                options: yesNoOptions,
                required: true,
                conditions: {
                  and: [
                    {
                      field: "hasSeacoastAndNotSeacoastSeaTerritory",
                      value: true,
                      operator: "equals"
                    }
                  ]
                }
              },
              ["mb-8"]
            ),
            useField(
              {
                type: "radio_button_group",
                preamble: "Trampoline on the premises?",
                key: "trampolineOnPremises",
                options: yesNoOptions,
                required: true,
                conditions: {
                  and: [
                    {
                      field: "hasColonialSeacoastSeaTerritoryOnly",
                      value: false,
                      operator: "equals"
                    }
                  ]
                }
              },
              ["mb-8"]
            ),
            useField({
              key: "trampolineApprovedFence",
              preamble: "Approved Fence?",
              type: "radio_button_group",
              options: yesNoOptions,
              conditions: {
                or: [
                  {
                    field: "trampolineOnPremises",
                    value: "Yes",
                    operator: "equals"
                  }
                ]
              },
              validations: [
                {
                  method: "custom",
                  error: "Risk Prohibited",
                  customValidator: (value: number) => {
                    return isPoolInApprovedFence(value, policyType);
                  }
                }
              ]
            })
            // useField(
            //   {
            //     type: "radio_button_group",
            //     preamble: "Is the dwelling on the seaward side of Hwy 59?",
            //     key: "dwellingOnSeawardIfOfHwy59",
            //     options: yesNoOptions,
            //     required: true,
            //     conditions: {
            //       and: [
            //         {
            //           field: "mCompanyLessThanOrEqual3",
            //           value: true,
            //           operator: "equals"
            //         }
            //       ]
            //     }
            //   },
            //   ["mb-8"]
            // )
          ],
          {
            title: "Dwelling Information"
          }
        )
      ],
      {
        title: "Property Information"
      }
    ),
    ...sharedUnderwritingQuestions(policyType, companyNumbers)
  ];
  const includesRelevantEndorsements = checkForEndorsements(
    endorsements as any[]
  );

  if (includesRelevantEndorsements && !isSeacoast) {
    homeOwnerForm.push(
      ...useFormSection(
        [
          ...useFormFieldGroup(
            [
              useSelectField({
                key: "waterLimit",
                label: "HO-470C Please select Water Limit",
                required: true,
                options: waterLimitOptions
              }),
              useSelectField({
                key: "limitedSlabFoundation",
                label: "HO-849 Limited Slab/Foundation (HO-A)",
                required: true,
                options: limitedSlabFoundationOptions
              })
            ],
            { layout: "2-col" }
          )
        ],
        {
          title: companyNumbers.includes(32)
            ? "Insurors Indemnity Select Limits"
            : "Roadrunner Statewide Limits"
        }
      )
    );
  }

  return homeOwnerForm;
}
